import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Paper, Pagination } from '@mui/material';
import { Message, ClassificationResult } from '@/types/api';
import MessageCard from './MessageCard';

interface MessageTimelineProps {
  messages: Message[];
  classifications?: Map<number, ClassificationResult>;
  onViewContext?: (messageId: number) => void;
  onLinkToLibrary?: (messageId: number, messageText?: string, fileName?: string) => void;
  page?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
}

const MessageTimeline: React.FC<MessageTimelineProps> = ({
  messages,
  classifications,
  onViewContext,
  onLinkToLibrary,
  page = 1,
  totalPages = 1,
  onPageChange,
}) => {
  const { t } = useTranslation('components');

  if (messages.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          {t('messageTimeline.noMessages')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('messageTimeline.noMessagesHint')}
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        {t('messageTimeline.messagesFound', { count: messages.length })}
      </Typography>

      {/* Messages List */}
      <Box>
        {messages.map((message) => {
          const classification = classifications?.get(message.message_id);
          return (
            <MessageCard
              key={message.id}
              message={message}
              classification={classification}
              onViewContext={onViewContext}
              onLinkToLibrary={onLinkToLibrary}
            />
          );
        })}
      </Box>

      {/* Pagination */}
      {totalPages > 1 && onPageChange && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, newPage) => onPageChange(newPage)}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
    </Box>
  );
};

export default MessageTimeline;
